import { LearningFeedPost, LearningFeedPostUserData, LearningFeedPostViewTelemetry } from "@/lib/types/feed";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface InteractedWithKeyword {
    keyword: string;
    translation?: string;
    timestamp: number;
    postId: string;
}

export interface FeedState {
    feedPosts: LearningFeedPost[];
    presentPostIds: string[];
    telemetry: {
        [key: string]: LearningFeedPostViewTelemetry;
    },
    interactedWithKeywords: InteractedWithKeyword[];
    currentOffset: number;
    currentLimit: number;
    selectedPost: LearningFeedPost | null;
    isPostAdminMenuOpen: boolean;
}

const initialState: FeedState = {
    feedPosts: [],
    interactedWithKeywords: [],
    presentPostIds: [],
    currentOffset: 0,
    currentLimit: 32,
    telemetry: {},  
    selectedPost: null,
    isPostAdminMenuOpen: false,
}
    
export const feedSlice = createSlice({
    name: 'feed',
    initialState,
    reducers: {
        appendFeedPosts: (state, action: PayloadAction<{posts: LearningFeedPost[], limitTo?: number}>) => {
            // if (action.payload.limitTo) {
            //     // Leave only the last N posts
            //     state.feedPosts = [...state.feedPosts, ...action.payload.posts].slice(-action.payload.limitTo);
            //     state.presentPostIds = [...state.presentPostIds, ...action.payload.posts.map(post => post._id)];
            // } else {
            state.feedPosts = [...state.feedPosts, ...action.payload.posts];
            state.presentPostIds = [...state.presentPostIds, ...action.payload.posts.map(post => post._id)];
            // }
        },
        setFeedPosts: (state, action: PayloadAction<LearningFeedPost[]>) => {
            state.feedPosts = action.payload;
            state.presentPostIds = action.payload.map(post => post._id);
        },
        addInteractedWithKeywords: (state, action: PayloadAction<InteractedWithKeyword[]>) => {
            state.interactedWithKeywords = [...state.interactedWithKeywords, ...action.payload];
        },
        setFeedPostViewTelemetry: (state, action: PayloadAction<{ postId: string, telemetry: LearningFeedPostViewTelemetry }>) => {
            state.telemetry[action.payload.postId] = action.payload.telemetry;
        },
        resetFeedState: (state) => {
            state.feedPosts = [];
            state.interactedWithKeywords = [];
            state.telemetry = {};
        },
        setSelectedPost: (state, action: PayloadAction<LearningFeedPost | null>) => {
            state.selectedPost = action.payload;
        },
        setShowPostAdminMenu: (state, action: PayloadAction<boolean>) => {
            state.isPostAdminMenuOpen = action.payload;
        },
        showPostAdminMenu: (state, action: PayloadAction<LearningFeedPost>) => {
            state.isPostAdminMenuOpen = true;            
            state.selectedPost = action.payload;
        },
        hidePostAdminMenu: (state) => {
            state.isPostAdminMenuOpen = false;
            state.selectedPost = null;
        },
        setFeedPostReactions: (state, action: PayloadAction<{ postId: string, reactions: string[], addReaction?: string[], removeReaction?: string[] }>) => {
            state.feedPosts = state.feedPosts.map(post => {                
                if (post._id == action.payload.postId) {
                    const postReactionCounters = post.reactions ?? {};                

                    for (const reaction of action.payload.addReaction ?? []) {
                        postReactionCounters[reaction] = (postReactionCounters[reaction] ?? 0) + 1;
                        postReactionCounters[reaction] = Math.min(999, postReactionCounters[reaction]);
                    }
    
                    for (const reaction of action.payload.removeReaction ?? []) {
                        postReactionCounters[reaction] = (postReactionCounters[reaction] ?? 0) - 1;
                        postReactionCounters[reaction] = Math.max(0, postReactionCounters[reaction]);
                    }

                    return { ...post, reactions: postReactionCounters, userData: { ...post.userData, reactions: action.payload.reactions } }   
                } else {
                    return post;
                }
            }) as LearningFeedPost[];
        },
        mergePostUserData: (state, action: PayloadAction<{ postId: string, userData: Partial<LearningFeedPostUserData> }>) => {
            state.feedPosts = state.feedPosts.map(post => {
                if (post._id == action.payload.postId) {
                    return { ...post, userData: { ...post.userData, ...action.payload.userData } };
                }
                return post;
            }) as LearningFeedPost[];
        }
    }
});

export const { appendFeedPosts, setFeedPosts, setFeedPostViewTelemetry, resetFeedState, setSelectedPost, setShowPostAdminMenu, showPostAdminMenu, hidePostAdminMenu, setFeedPostReactions, mergePostUserData, addInteractedWithKeywords } = feedSlice.actions;
export default feedSlice.reducer;